import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Save } from 'lucide-react';
import { useDesignerStore } from '../editor/store/designerStore';
import { parseRDF } from '../editor/lib/rdf/parser';
import { serializeToRDF } from '../editor/lib/rdf/serializer';
import { coordinatesFromEntity, isComplete, type FileCoordinates } from '../gitlab/coordinates';
import { loadOntologyFile, saveOntologyFile, StaleFileError } from '../gitlab/files';
import { findUnsupportedConstructs, type UnsupportedConstruct } from '../rdf/unsupported';
import { SaveDialog } from './SaveDialog';

interface Props {
  entity: unknown;
}

interface LoadedState {
  content: string;
  lastCommitId: string;
  unsupported: UnsupportedConstruct[];
}

interface Status {
  message: string;
  href?: string;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function GitLabBar({ entity }: Props) {
  const fromEntity = useMemo(() => coordinatesFromEntity(entity), [entity]);
  const [coordinates, setCoordinates] = useState<Partial<FileCoordinates>>(fromEntity);
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [busy, setBusy] = useState<'load' | 'save' | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const loadDraft = useDesignerStore((state) => state.loadDraft);
  const ontology = useDesignerStore((state) => state.ontology);

  useEffect(() => {
    setCoordinates(fromEntity);
  }, [fromEntity]);

  const update = (field: keyof FileCoordinates) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setCoordinates((current) => ({ ...current, [field]: event.target.value }));
  };

  const load = useCallback(async () => {
    if (!isComplete(coordinates)) return;

    setBusy('load');
    setError(null);
    setStatus(null);

    try {
      const file = await loadOntologyFile(coordinates);
      const { ontology: parsed } = parseRDF(file.content);

      loadDraft(parsed);
      setLoaded({
        content: file.content,
        lastCommitId: file.lastCommitId,
        unsupported: findUnsupportedConstructs(file.content),
      });
      setStatus({ message: `Loaded ${coordinates.path}` });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }, [coordinates, loadDraft]);

  const save = useCallback(async () => {
    if (!isComplete(coordinates) || !loaded) return;

    setBusy('save');
    setError(null);

    try {
      const result = await saveOntologyFile(
        coordinates,
        serializeToRDF(ontology),
        loaded.lastCommitId,
        ontology.name,
      );

      setReviewing(false);
      setStatus({ message: 'Merge request opened', href: result.mergeRequestUrl });
    } catch (caught) {
      // The edited buffer is never discarded, whatever went wrong.
      setError(caught instanceof StaleFileError ? caught.message : errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }, [coordinates, loaded, ontology]);

  return (
    <div className="oe-bar">
      <label className="oe-field">
        <span>Project</span>
        <input
          name="gitlab-project"
          autoComplete="off"
          spellCheck={false}
          value={coordinates.project ?? ''}
          onChange={update('project')}
          placeholder="group/ontologies"
        />
      </label>
      <label className="oe-field">
        <span>File</span>
        <input
          name="ontology-file-path"
          autoComplete="off"
          spellCheck={false}
          value={coordinates.path ?? ''}
          onChange={update('path')}
          placeholder="models/domain.rdf"
        />
      </label>
      <label className="oe-field oe-field-narrow">
        <span>Branch</span>
        <input
          name="gitlab-ref"
          autoComplete="off"
          spellCheck={false}
          value={coordinates.ref ?? ''}
          onChange={update('ref')}
          placeholder="main"
        />
      </label>

      <button className="oe-btn" onClick={load} disabled={!isComplete(coordinates) || busy !== null}>
        {busy === 'load' ? <Loader2 size={14} className="oe-spin" /> : <Download size={14} />}
        Load
      </button>
      <button className="oe-btn oe-btn-primary" onClick={() => setReviewing(true)} disabled={!loaded || busy !== null}>
        <Save size={14} />
        Save…
      </button>

      {status && (
        <span className="oe-status">
          {status.message}
          {status.href && (
            <>
              {': '}
              <a href={status.href} target="_blank" rel="noopener noreferrer">{status.href}</a>
            </>
          )}
        </span>
      )}
      {error && !reviewing && <span className="oe-error">{error}</span>}

      {reviewing && loaded && (
        <SaveDialog
          original={loaded.content}
          updated={serializeToRDF(ontology)}
          unsupported={loaded.unsupported}
          saving={busy === 'save'}
          error={error}
          onConfirm={save}
          onCancel={() => setReviewing(false)}
        />
      )}
    </div>
  );
}
